/**
 * Product hierarchy — data access for `product_nodes` (migration 719).
 *
 * Reads and writes go through the typed client so schema drift is a compile
 * error. `path` and `depth` are trigger-maintained: this module never writes
 * them, and a move is just a `parent_id` update.
 *
 * Authority: RLS gives members SELECT and workspace admins the writes. Not an
 * authority transition (§2.3a) — a structural register, edited from Settings.
 */

import { getTypedSupabaseClient } from '../typedSupabaseClient';
import type { Database } from '../../types/database.generated';
import type { ProductNode, ProductNodeKind } from './hierarchyTypes';

type ProductNodeRow = Database['public']['Tables']['product_nodes']['Row'];

export function toProductNode(row: ProductNodeRow): ProductNode {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    parentId: row.parent_id,
    name: row.name,
    kind: row.kind as ProductNodeKind,
    description: row.description,
    externalId: row.external_id,
    path: row.path ?? [],
    depth: row.depth,
    sortOrder: row.sort_order,
    active: row.active,
    metadata: (row.metadata as Record<string, unknown> | null) ?? {},
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export async function listProductNodes(
  workspaceId: string,
  opts: { includeInactive?: boolean } = {},
): Promise<ProductNode[]> {
  const db = getTypedSupabaseClient();
  let q = db
    .from('product_nodes')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('depth', { ascending: true })
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });
  if (!opts.includeInactive) q = q.eq('active', true);
  const { data, error } = await q;
  if (error) throw new Error(`Failed to load product hierarchy: ${error.message}`);
  return (data ?? []).map(toProductNode);
}

export interface CreateProductNodeInput {
  workspaceId: string;
  name: string;
  kind: ProductNodeKind;
  parentId?: string | null;
  description?: string | null;
  externalId?: string | null;
  sortOrder?: number;
}

export async function createProductNode(input: CreateProductNodeInput): Promise<ProductNode> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db
    .from('product_nodes')
    .insert({
      workspace_id: input.workspaceId,
      name: input.name.trim(),
      kind: input.kind,
      parent_id: input.parentId ?? null,
      description: input.description?.trim() || null,
      external_id: input.externalId?.trim() || null,
      sort_order: input.sortOrder ?? 0,
    })
    .select('*')
    .single();
  if (error) throw new Error(friendlyProductNodeError(error.message));
  return toProductNode(data);
}

export interface UpdateProductNodeInput {
  name?: string;
  kind?: ProductNodeKind;
  /** `null` moves the node to the root. */
  parentId?: string | null;
  description?: string | null;
  externalId?: string | null;
  sortOrder?: number;
  active?: boolean;
}

export async function updateProductNode(id: string, patch: UpdateProductNodeInput): Promise<ProductNode> {
  const db = getTypedSupabaseClient();
  const update: Database['public']['Tables']['product_nodes']['Update'] = {};
  if (patch.name !== undefined) update.name = patch.name.trim();
  if (patch.kind !== undefined) update.kind = patch.kind;
  if (patch.parentId !== undefined) update.parent_id = patch.parentId;
  if (patch.description !== undefined) update.description = patch.description?.trim() || null;
  if (patch.externalId !== undefined) update.external_id = patch.externalId?.trim() || null;
  if (patch.sortOrder !== undefined) update.sort_order = patch.sortOrder;
  if (patch.active !== undefined) update.active = patch.active;
  const { data, error } = await db
    .from('product_nodes')
    .update(update)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw new Error(friendlyProductNodeError(error.message));
  return toProductNode(data);
}

/**
 * Retire a node. Structure nodes are never deleted (mig 727): the node keeps
 * its id, its name and every item placed on it, and disappears from pickers.
 * The server refuses while the node still has active children.
 */
export async function retireProductNode(id: string, reason?: string): Promise<void> {
  const db = getTypedSupabaseClient();
  const { error } = await db.rpc('retire_structure_node', {
    p_kind: 'product',
    p_node_id: id,
    p_reason: reason?.trim() || undefined,
  });
  if (error) throw new Error(friendlyProductNodeError(error.message));
}

/** Bring a retired node back. The server refuses while its parent is still retired. */
export async function restoreProductNode(id: string): Promise<void> {
  const db = getTypedSupabaseClient();
  const { error } = await db.rpc('restore_structure_node', { p_kind: 'product', p_node_id: id });
  if (error) throw new Error(friendlyProductNodeError(error.message));
}

/** Count items placed directly on each node (for the admin tree's badges). */
export async function countItemsByProductNode(workspaceId: string): Promise<Map<string, number>> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db
    .from('items')
    .select('product_node_id')
    .eq('workspace_id', workspaceId)
    .not('product_node_id', 'is', null);
  if (error) throw new Error(`Failed to count items per product node: ${error.message}`);
  const out = new Map<string, number>();
  for (const row of data ?? []) {
    const id = row.product_node_id;
    if (!id) continue;
    out.set(id, (out.get(id) ?? 0) + 1);
  }
  return out;
}

/** Turn the trigger's and index's messages into something a person can act on. */
export function friendlyProductNodeError(message: string): string {
  if (/uq_product_nodes_sibling_name/i.test(message)) {
    return 'A node with that name already exists under the same parent.';
  }
  if (/would create a cycle|cannot be its own parent/i.test(message)) {
    return 'A node cannot be moved under itself or one of its own descendants.';
  }
  if (/same workspace/i.test(message)) {
    return 'The parent must belong to the same workspace.';
  }
  if (/row-level security/i.test(message)) {
    return 'Only a workspace admin can change the product hierarchy.';
  }
  return message;
}
